import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { SEOHead } from '@/components/SEOHead';

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <>
      <SEOHead
        title="Privacy Policy - Vergo"
        description="Read Vergo's privacy policy to understand how we collect, use, and protect your personal data."
      />
      <div className="min-h-screen bg-background py-8 px-4">
        <div className="max-w-3xl mx-auto">
          <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back
          </Button>
          <Card>
            <CardHeader>
              <CardTitle className="text-3xl">Privacy Policy</CardTitle>
              <p className="text-sm text-muted-foreground">Last updated: April 5, 2026</p>
            </CardHeader>
            <CardContent className="prose prose-sm dark:prose-invert max-w-none space-y-6">
              <section>
                <h2 className="text-xl font-semibold">1. Information We Collect</h2>
                <p>When you create an account, we collect your email address, username, and password (securely hashed). We also collect usage data such as search queries, bot interactions, and preferences to improve our services.</p>
                <p>We may also automatically collect:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>IP address and approximate geolocation</li>
                  <li>Browser type, device type, and operating system</li>
                  <li>Pages visited, time spent, and referral source</li>
                  <li>Search queries and interaction patterns within the platform</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold">2. How We Use Your Information</h2>
                <ul className="list-disc pl-6 space-y-1">
                  <li>To provide and maintain our AI chatbot directory service</li>
                  <li>To personalize your experience with AI-powered recommendations</li>
                  <li>To process your account and manage subscriptions</li>
                  <li>To communicate service updates and respond to inquiries</li>
                  <li>To detect and prevent fraud and abuse</li>
                  <li>To display relevant advertisements via Google AdSense</li>
                  <li>To analyze usage trends and improve our services</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold">3. Legal Basis for Processing (GDPR)</h2>
                <p>If you are in the European Economic Area (EEA), we process your personal data based on:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li><strong>Consent</strong> — for cookies, advertising, and marketing communications</li>
                  <li><strong>Contractual necessity</strong> — to provide the service you signed up for</li>
                  <li><strong>Legitimate interest</strong> — for analytics, fraud prevention, and service improvement</li>
                  <li><strong>Legal obligation</strong> — to comply with applicable laws</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold">4. Cookies & Tracking Technologies</h2>
                <p>We use the following types of cookies:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li><strong>Essential cookies</strong> — required for authentication, session management, and security (e.g., Supabase auth tokens). These cannot be disabled.</li>
                  <li><strong>Analytics cookies</strong> — help us understand how visitors interact with the site to improve user experience.</li>
                  <li><strong>Advertising cookies</strong> — set by Google AdSense to serve personalized or contextual advertisements. These may track you across websites.</li>
                </ul>
                <p>You can manage your cookie preferences using the cookie consent banner displayed on your first visit, or through your browser settings. Withdrawing consent for non-essential cookies will not affect the functionality of essential site features.</p>
              </section>

              <section>
                <h2 className="text-xl font-semibold">5. Data Sharing & Third Parties</h2>
                <p>We do not sell your personal data. We share data only with the following service providers who process data on our behalf:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li><strong>Supabase</strong> — authentication, database storage, and edge functions</li>
                  <li><strong>Google AdSense</strong> — advertising (may use cookies for personalized ads; see <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary underline">Google's Privacy Policy</a>)</li>
                  <li><strong>Stripe / Paymob</strong> — payment processing for subscriptions</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold">6. International Data Transfers</h2>
                <p>Your data may be transferred to and processed in countries outside your country of residence, including the United States. We ensure appropriate safeguards are in place, such as Standard Contractual Clauses (SCCs), to protect your data in accordance with applicable laws.</p>
              </section>

              <section>
                <h2 className="text-xl font-semibold">7. Data Security</h2>
                <p>We implement industry-standard security measures including:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Encryption in transit (TLS/SSL)</li>
                  <li>Row-Level Security (RLS) policies on our database</li>
                  <li>Rate limiting on API endpoints</li>
                  <li>Rate limiting and abuse monitoring on authentication and API endpoints</li>
                  <li>Regular security scans and vulnerability assessments</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold">8. Your Rights</h2>
                <p>Depending on your location, you may have the following rights:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li><strong>Access</strong> — request a copy of your personal data</li>
                  <li><strong>Rectification</strong> — correct inaccurate or incomplete data</li>
                  <li><strong>Erasure</strong> — request deletion of your personal data ("right to be forgotten")</li>
                  <li><strong>Restriction</strong> — request we limit processing of your data</li>
                  <li><strong>Portability</strong> — receive your data in a structured, machine-readable format</li>
                  <li><strong>Objection</strong> — object to processing based on legitimate interests</li>
                  <li><strong>Withdraw consent</strong> — withdraw previously given consent at any time</li>
                </ul>
                <p>You can exercise most of these rights through your account settings. For data export or deletion requests, please contact us.</p>
              </section>

              <section>
                <h2 className="text-xl font-semibold">9. Data Retention</h2>
                <p>We retain your data for as long as your account is active. Upon account deletion, your personal data is removed within 30 days, except where retention is required by law or for legitimate business purposes (e.g., fraud prevention).</p>
              </section>

              <section>
                <h2 className="text-xl font-semibold">10. Children's Privacy</h2>
                <p>Vergo is not intended for users under the age of 16. We do not knowingly collect data from children. If we become aware that we have collected data from a child under 16, we will take steps to delete it promptly.</p>
              </section>

              <section>
                <h2 className="text-xl font-semibold">11. Changes to This Policy</h2>
                <p>We may update this policy from time to time. We will notify registered users of significant changes via email. Continued use of the service after changes constitutes acceptance of the updated policy.</p>
              </section>

              <section>
                <h2 className="text-xl font-semibold">12. Contact Us</h2>
                <p>If you have questions about this privacy policy or wish to exercise your data rights, please contact us at:</p>
                <p className="font-medium">Email: privacy@vergoai.app</p>
              </section>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}
